'use client';

import clsx from 'clsx';
import React from 'react';
import Icon from './Icon';

/**
 * DS `Dialogs` — TR - Design System [MASTER], node 35:5664.
 *
 * The board draws four dialogs (place bid, you're set, add address, add card).
 * They share one shell and differ only in body and actions, so this is a
 * shell plus slots rather than four components.
 *
 * Rebuilt against the master. The previous version came from `TR - Update-01`,
 * a WIP file, and got the shell wrong in three ways: it sat on surface-000
 * with a 1px border and no shadow, on the assumption that the system's
 * no-shadow rule covered dialogs too. The master says otherwise — modals are
 * the one place a shadow appears, via the named effect style "Big DS":
 *
 *   background   BG/100 (#f7f7f4), no border
 *   shadow       0 5px 25px rgba(0,0,0,0.25)
 *   radius       2px, the system's card radius
 *   box          500px wide, 24px padding, 24px between sections
 *   title        Header/H2 (20/30 Display Medium) on Text/400
 *   close        56px box on the top-right corner, 20px glyph, no fill
 *
 * The close button is a corner overlay rather than a header child, so the
 * title block keeps its full width and the hit area stays 56px even next to a
 * one-line title. The header reserves its width so a long title can't run
 * under the glyph.
 */
export const Dialog = ({
    open,
    title,
    subtitle,
    onClose,
    children,
    actions,
    footnote,
    width = 500,
    className
}: {
    open: boolean;
    title: string;
    subtitle?: React.ReactNode;
    onClose?: () => void;
    children?: React.ReactNode;
    /** Action row, pinned to the foot and full-bleed. */
    actions?: React.ReactNode;
    /** Legal or helper line beneath the actions. */
    footnote?: React.ReactNode;
    width?: number;
    className?: string;
}) => {
    if (!open) return null;

    return (
        <div
            className={clsx(
                'relative flex max-h-full flex-col gap-[24px] rounded-ds bg-surface-100 p-[24px] shadow-ds-big',
                // Modals keep transform-origin: center — they are not
                // anchored to a trigger the way popovers are.
                'origin-center duration-modal ease-ds-out',
                className
            )}
            style={{ width }}
            role="dialog"
            aria-modal="true"
            aria-label={title}
        >
            <div className={clsx('flex flex-col justify-center', onClose && 'pr-[32px]')}>
                <h2 className="text-h2 text-ink-400">{title}</h2>
                {subtitle && <p className="text-body-400 text-ink-300">{subtitle}</p>}
            </div>

            {onClose && (
                <button
                    type="button"
                    aria-label="Close"
                    onClick={onClose}
                    className="absolute right-0 top-0 flex size-[56px] items-center justify-center text-ink-400"
                >
                    <Icon name="close" size={20} />
                </button>
            )}

            {children && <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>}

            {actions && <div className="flex shrink-0 gap-[8px]">{actions}</div>}

            {footnote && <p className="text-h5 text-ink-200">{footnote}</p>}
        </div>
    );
};

/**
 * Scrim. Separate from Dialog so a page can position the panel itself — the
 * board centres some dialogs and bottom-sheets others on mobile.
 */
export const DialogScrim = ({
    children,
    onDismiss,
    className
}: {
    children: React.ReactNode;
    onDismiss?: () => void;
    className?: string;
}) => (
    <div className={clsx('fixed inset-0 z-[50] flex items-center justify-center p-[16px]', className)}>
        <button
            type="button"
            aria-label="Dismiss"
            onClick={onDismiss}
            className="absolute inset-0 bg-surface-600/50 duration-modal ease-ds-out"
        />
        <div className="relative">{children}</div>
    </div>
);

export default Dialog;
